import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@wayflyer/auth-sdk";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import { getStageCompletionStatusQueryOptions } from "shared-ui";

import { VALIDATED_DOCUMENT_TYPE_UPLOADS_API, VALIDATED_DOCUMENT_UPLOADS_API } from "../../components/constants";
import { UploadDocumentType } from "../../types";

/** GET list of all uploaded validated documents */
export const useValidatedDocumentQuery = (companySlug: string, applicationId: number, isEnabled = true) => {
  const client = useAxios();

  const fetchUrl = VALIDATED_DOCUMENT_UPLOADS_API.replace(":customerSlug", companySlug).replace(
    ":applicationId",
    applicationId.toString()
  );

  const queryFn = () => client.get<FundingApplicationSchemas.UploadedDocumentsSchema>(fetchUrl);

  return useQuery({
    queryKey: [fetchUrl],
    queryFn,
    select: ({ data }) => data.documents,
    enabled: isEnabled,
  });
};

/** POST request to kick off document validation */
export const useTriggerValidation = (companySlug: string, applicationId: number, documentType: UploadDocumentType) => {
  const client = useAxios();
  const queryClient = useQueryClient();

  const url = `${VALIDATED_DOCUMENT_TYPE_UPLOADS_API}/validate`
    .replace(":customerSlug", companySlug)
    .replace(":applicationId", applicationId.toString())
    .replace(":documentType", documentType);

  const mutationFn = (documentIds: string[]) =>
    client.post<FundingApplicationSchemas.ValidateRequestSchema>(url, { documentIds });

  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getStageCompletionStatusQueryOptions(companySlug).queryKey });
    },
  });
};

/** POST request to mark files as user-verified (if issues come up after that, we ignore them) */
export const useVerifyDocuments = (companySlug: string, applicationId: number, documentType: UploadDocumentType) => {
  const client = useAxios();
  const queryClient = useQueryClient();

  const url = `${VALIDATED_DOCUMENT_TYPE_UPLOADS_API}/verify`
    .replace(":customerSlug", companySlug)
    .replace(":applicationId", applicationId.toString())
    .replace(":documentType", documentType);

  const mutationFn = (documentIds: string[]) =>
    client.post<FundingApplicationSchemas.VerifyRequestSchema>(url, { documentIds });

  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getStageCompletionStatusQueryOptions(companySlug).queryKey });
    },
  });
};
